import { z } from 'zod';
import { INSTALLATION_MODES } from '../constants.js';
import { httpUrlSchema } from './common.js';
import { ghostBulkEmailSchema, ghostMailSchema } from './ghost-config.js';
/**
 * Installation d'un site neuf, de l'instance nue au site en ligne.
 *
 * Ce que la personne fournit tient en un formulaire, et c'est délibéré : tout ce qui peut être
 * déduit l'est. Le système, l'architecture, les privilèges, les adresses IP, la place disque et la
 * mémoire sont relevés sur la cible ; le port du site n'existe plus (la bordure parle au conteneur
 * par le réseau Docker) ; la racine des projets est constante.
 *
 * Ne restent que les décisions qu'aucune sonde ne peut prendre à la place de quelqu'un : le nom et
 * l'adresse du site, la version de Ghost, l'identité du propriétaire, et — facultativement — par où
 * partent les courriels.
 */
/**
 * Courriel transactionnel et infolettres, facultatifs à l'installation.
 *
 * Facultatifs mais lourds de conséquences : **un Ghost sans courriel transactionnel dont la
 * vérification d'appareil reste active enferme son équipe dehors** au premier changement de machine,
 * puisque le code de vérification part par courriel. Quand rien n'est configuré, l'installateur
 * écrit donc explicitement `security.staffDeviceVerification: false` et le dit sur l'écran final,
 * plutôt que de laisser le défaut de Ghost transformer un site neuf en site inaccessible.
 */
export const courrielInstallationSchema = z.object({
    mail: ghostMailSchema.optional(),
    bulkEmail: ghostBulkEmailSchema.optional(),
});
/**
 * Le propriétaire du site Ghost, créé par Vigie.
 *
 * Le mot de passe est **choisi dans le navigateur** — engendré à l'ouverture du formulaire, visible
 * et modifiable — et non côté serveur. Un secret que le serveur engendrerait ne pourrait être montré
 * qu'une fois, dans un écran rafraîchi toutes les dix secondes : il se perdrait au premier
 * rechargement d'onglet. Et sans courriel configuré, Ghost n'offre aucune réinitialisation.
 */
export const proprietaireSchema = z.object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().email(),
    password: z.string().min(10, 'Au moins 10 caractères').max(200),
});
export const installationInputSchema = z.object({
    ghostVersion: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version sémantique attendue (x.y.z)'),
    /** Rediriger `www.<hôte>` vers l'adresse du site. */
    www: z.boolean().default(false),
    owner: proprietaireSchema,
    courriel: courrielInstallationSchema.optional(),
    /**
     * Mot de passe sudo, si le compte n'est ni root ni sudo sans mot de passe.
     *
     * Il ne sert qu'à l'étape d'installation de Docker, transite par l'entrée standard de `sudo -S`
     * (jamais par la ligne de commande, visible dans la table des process), et est effacé de la ligne
     * d'installation dès cette étape terminée.
     */
    sudoPassword: z.string().min(1).max(200).optional(),
    mode: z.enum(INSTALLATION_MODES).default('production'),
});
/**
 * Ce que l'installation garde chiffré entre ses étapes.
 *
 * Une installation dure longtemps et traverse plusieurs connexions SSH : ce qu'une étape découvre,
 * les suivantes en ont besoin. Tout ce qui est secret vit donc ici, dans une seule colonne chiffrée
 * — et **chaque étape retire ce dont la suite n'a plus besoin** (cf. `secretsAttendus`). Un secret
 * qui traîne après son dernier usage est un secret de trop.
 */
export const donneesInstallationSchema = z.object({
    /** De quoi rouvrir une connexion SSH à chaque étape, puis enregistrer le site. */
    auth: z.object({
        privateKey: z.string().optional(),
        passphrase: z.string().optional(),
        password: z.string().optional(),
    }),
    sudoPassword: z.string().optional(),
    owner: proprietaireSchema.optional(),
    courriel: courrielInstallationSchema.optional(),
    /** Engendrés à l'étape « pile », transmis au site comme identifiants de base. */
    mysql: z
        .object({
        password: z.string(),
        rootPassword: z.string(),
    })
        .optional(),
    /** Obtenue à l'étape « ghost » par l'intégration créée dans Ghost, au format `id:secret`. */
    adminApiKey: z.string().optional(),
    /** Relevé à la préparation : l'identité et le système de la cible. */
    cible: z
        .object({
        uid: z.number().int(),
        gid: z.number().int(),
        privileges: z.enum(['root', 'sudo', 'sudo_mdp']),
        systeme: z.object({ id: z.string(), codename: z.string() }),
        architecture: z.string(),
        homeDir: z.string(),
    })
        .optional(),
});
/** Vue publique d'une installation : tout sauf ce qui est chiffré. */
export const installationPublicSchema = z.object({
    id: z.string().uuid(),
    siteId: z.string().uuid().nullable(),
    status: z.string(),
    mode: z.enum(INSTALLATION_MODES),
    siteName: z.string(),
    url: httpUrlSchema,
    slug: z.string(),
    ghostVersion: z.string(),
    www: z.boolean(),
    sshHost: z.string(),
    sshUser: z.string(),
    ownerEmail: z.string(),
    log: z.string(),
    avertissements: z.array(z.string()),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
});
//# sourceMappingURL=installation.js.map